/**
 * The manufacturing hash (plan §4.2): SHA-256 over the canonical parts and joints (and the
 * openings a family publishes, so a moved shelf datum always changes it).
 *
 * Canonical form, chosen so the first slice's pinned hashes stay valid byte-for-byte:
 * - parts: the fields that say what is cut and where it goes (`id role qty material
 *   thicknessTicks finished cut grainAxis grainWorld face edge placement datum shelfNo`),
 *   plus `glueUp` and `bay` only when present. `stockId` is a catalogue reference, like the
 *   profile version, and is left out: the stock's material text and thickness are in.
 * - openings: `index clear bottomDatum topDatum` (+ `bay` when present).
 * - joints: every joint that machines a part (housed dado, rebate, dowels, pocket screws,
 *   egg-crate). A screwed butt between two touching parts is fully determined by the parts'
 *   placements, so it adds nothing and is omitted; any other kind enters the payload, and the
 *   payload gains a `joints` key only when there is at least one. A plain screwed carcass
 *   therefore keeps its first-slice hash, and changing any joint to a machined one changes it.
 * - pulls: the knobs and bar handles on doors and drawers (src/families/fronts.ts), by kind and
 *   position, since each drills its part and is bought by name. The payload gains a `pulls` key
 *   only when there is at least one, so a design without fronts keeps its hash.
 * `canonicalJson` drops `undefined`, so optional fields never perturb the hash.
 */
import { hashOf } from '../domain/hash';
import type { AssemblyPart, Hardware, Joint } from './assembly';

export interface HashableOpening {
  index?: number;
  clear?: number;
  bottomDatum?: number;
  topDatum?: number;
  bay?: number;
  id?: string;
  clearMm?: number;
  bounds?: { x0: number; x1: number; y0: number; y1: number };
}

export interface Hashable {
  parts: readonly AssemblyPart[] | readonly Omit<AssemblyPart, 'stockId'>[];
  openings: readonly HashableOpening[];
  joints?: readonly Joint[];
  hardware?: readonly Hardware[];
}

/** Joints that are not implied by the parts' placements. */
export const isMachinedJoint = (j: Pick<Joint, 'kind'>): boolean => j.kind !== 'screwed-butt';

/** Hardware that drills a part where the parts and joints alone do not say so. */
const PULLS = new Set(['knob', 'bar-handle']);

export function manufacturingPayload(a: Hashable): unknown {
  const pulls = (a.hardware ?? []).filter((h) => PULLS.has(h.kind) && h.at.length).map((h) => ({ kind: h.kind, at: h.at }));
  const machined = (a.joints ?? []).filter(isMachinedJoint).map((j) => ({ id: j.id, a: j.a, b: j.b, kind: j.kind, face: j.face, datumMm: j.datumMm }));
  return {
    parts: a.parts.map((p) => ({
      id: p.id, role: p.role, qty: p.qty, material: p.material, thicknessTicks: p.thicknessTicks,
      finished: p.finished, cut: p.cut, grainAxis: p.grainAxis, grainWorld: p.grainWorld,
      face: p.face, edge: p.edge, placement: p.placement, datum: p.datum, shelfNo: p.shelfNo,
      glueUp: p.glueUp, bay: p.bay,
    })),
    openings: a.openings.map((o) =>
      o.index !== undefined
        ? { index: o.index, clear: o.clear, bottomDatum: o.bottomDatum, topDatum: o.topDatum, bay: o.bay }
        : { id: o.id, clearMm: o.clearMm, bounds: o.bounds }),
    ...(machined.length ? { joints: machined } : {}),
    ...(pulls.length ? { pulls } : {}),
  };
}

export const manufacturingHash = (a: Hashable): string => hashOf(manufacturingPayload(a));
